// WebSocket client for the chat-driven charting + dashboard agents. Owns the
// connection, accumulates streamed assistant tokens (progressive rendering), and
// exposes the latest spec, the board being built, the progress trail and the
// suggested next actions.
import { useCallback, useEffect, useRef, useState } from 'react';

import { getAccessToken, getStoredToken } from '../../../lib/auth';
import { appEnv } from '../../../lib/env';
import type { ChartDataResponse } from '../lib/types';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  streaming?: boolean;
  /** A turn the user stopped, or one the server reported as failed. */
  tone?: 'error' | 'stopped';
}

export interface Suggestion {
  label: string;
  prompt: string;
}

/** One step the agent reported. Kept as a trail, not a single replaced line. */
export interface ProgressStep {
  id: number;
  label: string;
  done: boolean;
}

export interface AgentDashboard {
  id: string;
  name: string | null;
  /** Public share URL, when the build published one. */
  embedUrl?: string | null;
  /** The board's tabs as the agent compiled them. */
  views?: Array<{ id: string; title: string }>;
  completion?: Record<string, unknown> | null;
  /** Bumped on every dashboard frame so consumers react to repeated updates. */
  nonce: number;
}

/** One source the build could not choose between, with grounding's own reason. */
export interface BuildCandidate {
  id: string;
  name: string;
  verdict?: string;
  why?: string;
}

/**
 * Why a turn stopped without a board, as DATA rather than prose.
 *
 * The agent has always answered a stop with a machine-readable gap naming the
 * recovery — and for `pick_source`, the candidates it could not choose between.
 * The socket used to send only the paragraph, so the only recovery the UI could
 * offer was "type the name of one of the sources we just listed", which is doing
 * by hand the thing the machine already did.
 */
/**
 * A question the build wants answered BEFORE it plans, with the answers it can
 * already see (AG-2329).
 *
 * ⚠️ `options` IS GENERATED, NEVER A FIXED LIST. The candidate views come from
 * the dimensions actually in the data, the filters from its real field names.
 * A hardcoded set of tab names here would be a second designer with worse
 * information than the first — which is the whole defect AG-2283 spent itself
 * removing.
 *
 * A plain string stays a valid question, so every existing `gap.questions`
 * sender keeps working and renders as it always did.
 */
export interface ScopeQuestion {
  id: string;
  question: string;
  hint?: string;
  /** `multi` → chips, any number. `single` → one choice. */
  kind?: 'multi' | 'single';
  options?: Array<{ value: string; label: string }>;
}

export interface BuildGap {
  recovery: string;
  reason: string;
  message: string;
  candidates: BuildCandidate[];
  questions: Array<string | ScopeQuestion>;
  /** The ask this gap answered, so a retry does not need it retyped. */
  ask: string;
}

type Frame =
  | { type: 'ready'; session_id: string }
  | { type: 'token'; delta: string }
  | { type: 'status'; phase: string; tool?: string; detail?: string }
  | { type: 'spec'; spec: Record<string, unknown>; chart_id: string | null }
  | { type: 'chart_update'; reason: string; data: ChartDataResponse }
  | {
      type: 'dashboard';
      dashboard_id: string;
      name: string | null;
      embed_url?: string | null;
      completion?: Record<string, unknown> | null;
      views?: Array<{ id: string; title: string }>;
    }
  | {
      type: 'gap';
      recovery: string;
      reason: string;
      message?: string;
      candidates?: BuildCandidate[];
      questions?: Array<string | ScopeQuestion>;
    }
  | { type: 'design_reference'; count: number }
  | { type: 'suggestions'; items: Suggestion[] }
  | { type: 'message'; text: string }
  | { type: 'transcript'; items: { role: 'user' | 'assistant'; text: string }[] }
  | { type: 'error'; message: string }
  | { type: 'pong' }
  | { type: 'resumed'; recovered: boolean; replayed: number; pending?: boolean }
  | { type: 'done' };

function wsUrl(sessionId: string, token: string): string {
  const path = `/v1/chart-engine/ws/${encodeURIComponent(sessionId)}?token=${encodeURIComponent(token)}`;
  // 1) Explicit override (e.g. ws://host:8010 or wss://api.example.com).
  const explicit = String(import.meta.env.VITE_CHART_WS_URL || '').trim().replace(/\/$/, '');
  if (explicit) return `${explicit}${path}`;
  // 2) Dev convenience: connect straight to the local charting backend so the
  //    Vite dev server does NOT need the `ws:true` proxy (one less restart).
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return `ws://localhost:8010${path}`;
  }
  // 3) Same-origin: rely on a reverse proxy (or Vite `ws:true`) forwarding /chart-api.
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}${appEnv.chartApiBaseUrl || '/chart-api'}${path}`;
}

/**
 * The credential this socket presents, in the order `apiFetch` presents them.
 *
 * The Identity Studio access token first — on a deployment with IAM that is the
 * session the backend verifies, and the socket is refused without it. This used
 * to read only the legacy platform key, which no sign-in has written since
 * Identity Studio arrived: the socket therefore opened with `token=` empty, the
 * handler could not name a caller, and the assistant showed "Disconnected" to
 * someone who was demonstrably signed in.
 *
 * The platform bearer remains the fallback, for a deployment with no IAM where
 * it is not a session at all — only what the agent forwards to read a source.
 */
function token(): string {
  return getAccessToken() || getStoredToken() || '';
}

/** Ceiling on the automatic-reconnect backoff. */
const MAX_RETRY_MS = 15_000;
/**
 * How long the socket may deliver NOTHING before this client declares it dead
 * (AG-2364).
 *
 * The server answers every `ping` with a `pong` and, during a turn, heartbeats
 * on its own 20s clock — so a socket that has delivered no frame of any kind for
 * three of those is not a quiet socket, it is a half-open one: the peer's leg is
 * gone and this browser was never told. Measured live 2026-09-21: the backend's
 * final `message`/`dashboard`/`done` frames failed with "client gone" while
 * nginx still held the browser's side open and the panel still showed Stop. The
 * held result only arrived after a reload, because a reload is the one thing
 * that reopens a socket the browser believes is fine. Closing it here routes
 * into `onclose` → reconnect → `init`, where the server hands the held frames
 * back (`resumed`, AG-2282).
 */
const SILENCE_MS = 60_000;
/** The backend's close code for a token it refused (see ws.py). */
const AUTH_CLOSE = 4401;
/**
 * Only blame the environment after this many failed attempts in a row. One slow
 * upgrade is a blip, not a broken deployment, and saying so on the first miss is
 * what made a two-second hiccup look like an outage.
 */
const UNREACHABLE_AFTER = 3;

/** Turn a backend phase name into something a person can read. */
function phaseLabel(frame: Extract<Frame, { type: 'status' }>): string {
  if (frame.tool) return `Running ${frame.tool.replace(/_/g, ' ')}`;
  const phase = String(frame.phase || '').replace(/_/g, ' ').trim();
  if (!phase) return 'Working';
  if (phase === 'thinking') return 'Thinking';
  return phase.charAt(0).toUpperCase() + phase.slice(1);
}

let messageSeq = 0;
const nextId = () => `m${++messageSeq}`;

export type ConnState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface SendDashboardOptions {
  /** Edit this board incrementally instead of building a new one. */
  dashboardId?: string;
  /** Restrict grounding to these data sources. */
  sourceIds?: string[];
  /** Publish a public share link as part of the build.
   *
   *  The server's own default is TRUE, which is how asking the assistant to edit
   *  a board produced a public, no-sign-in link nobody had asked for and opened
   *  it in a tab (AG-2031). Publishing to an unauthenticated URL is a disclosure
   *  action — it takes whatever the board shows and puts it where anyone holding
   *  the link can read it — so this client states the flag on EVERY turn and
   *  states it false unless a person asked for a link. */
  share?: boolean;
}

export interface ChartSocketOptions {
  initSpec?: Record<string, unknown>;
  chartId?: string;
  getSpec?: () => Record<string, unknown> | null | undefined;
}

export function useChartSocket(sessionId: string | null, opts?: ChartSocketOptions) {
  const [connState, setConnState] = useState<ConnState>('idle');
  const [connError, setConnError] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [progress, setProgress] = useState<ProgressStep[]>([]);
  const [busy, setBusyState] = useState(false);
  // `busy` as a ref too: `onclose` is a closure over the render that opened the
  // socket, so reading the state there would see whatever was true then.
  const busyRef = useRef(false);
  const setBusy = useCallback((v: boolean) => { busyRef.current = v; setBusyState(v); }, []);
  const [chart, setChart] = useState<ChartDataResponse | null>(null);
  const [spec, setSpec] = useState<Record<string, unknown> | null>(null);
  const [dashboard, setDashboard] = useState<AgentDashboard | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [designReferences, setDesignReferences] = useState(0);
  const [gap, setGap] = useState<BuildGap | null>(null);
  /** The last dashboard ask, so a recovery can re-run it verbatim. */
  const lastAskRef = useRef('');

  const wsRef = useRef<WebSocket | null>(null);
  const connErrorSeen = useRef(false);
  // Automatic reconnect. A dropped socket used to park the panel on
  // "Disconnected" until somebody clicked Reconnect or reloaded the page — so a
  // proxy blip, a laptop waking from sleep or a backend deploy all reached the
  // user as "the assistant is broken, restart the app". The socket is cheap to
  // reopen and the conversation lives server-side under the session id, so
  // recovery is ours to do, not the user's.
  const retryRef = useRef(0);
  const retryTimerRef = useRef<number | undefined>(undefined);
  const authFailedRef = useRef(false);
  // The caller's latest options, read inside the socket's callbacks (`onopen`
  // sends the on-screen chart). Written in an effect, because a socket callback
  // always runs after the commit that changed them — and the ref starts out
  // holding the first render's value, so the opening frame is never stale.
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  }, [opts]);
  // Bumping this reopens the socket — how `stop()` cancels an in-flight turn and
  // how `reconnect()` recovers from a dropped one.
  const [epoch, setEpoch] = useState(0);
  const stoppingRef = useRef(false);
  /** A turn that was in flight when the socket closed. Held until the reconnect
   *  says whether the server recovered it — see `resumed` (AG-2178). */
  const pendingTurnRef = useRef(false);
  /** The transcript id of a "reconnected without finding a finished board"
   *  report, while it stands. The turn's own frames can still arrive on a later
   *  socket (the server holds them per session, AG-2282) — and when they do the
   *  report was wrong and comes down. Measured live 2026-09-21: a red "send the
   *  request again" sat above the finished board's own reply. */
  const lostTurnRef = useRef<string | null>(null);
  const stepSeq = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    // No token is NOT a reason to refuse the connection here.
    //
    // Whether one is required is the deployment's answer, not this hook's: with
    // IAM configured the backend refuses the socket and says so in an `error`
    // frame, which is a better message than any this file could invent; without
    // it the assistant starts, and a tool that needs a credential fails with the
    // platform's own message. Guessing in advance gets both cases wrong.
    const t = token();
    let alive = true;
    // This effect's whole job is to open a socket, and the panel has to say so
    // while it does. The state IS the external system's status: there is no
    // render-time value to derive "we are dialling" from.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reports the transport state this effect creates; see above
    setConnState('connecting');
    setConnError('');
    connErrorSeen.current = false;
    const ws = new WebSocket(wsUrl(sessionId, t));
    wsRef.current = ws;

    // If the upgrade never completes (backend down, or the dev proxy is missing
    // `ws: true`), say so rather than spinning forever.
    const timeout = window.setTimeout(() => {
      if (alive && ws.readyState !== WebSocket.OPEN) {
        // Closing here routes into `onclose`, which schedules the retry. The
        // environment advice waits until retrying has plainly not helped.
        if (retryRef.current >= UNREACHABLE_AFTER) {
          setConnError(
            'Could not reach the assistant. Check that the analytics backend is running on :8010 and that the dev server was restarted after enabling the WebSocket proxy.',
          );
        }
        try {
          ws.close();
        } catch {
          /* noop */
        }
      }
    }, 6000);

    // A long tool phase can leave the socket silent for minutes; an idle-timing
    // reverse proxy would cut it. The backend answers `ping` with `pong`.
    let keepalive: number | undefined;
    // When the last frame of ANY kind arrived, and whether this client closed
    // the socket for going silent — a close it asked for that must still be
    // treated as a drop, because the point of asking was to reconnect.
    let lastFrameAt = 0;
    let silenced = false;

    ws.onopen = () => {
      if (!alive) return;
      window.clearTimeout(timeout);
      retryRef.current = 0;
      authFailedRef.current = false;
      setConnError('');
      setConnState('open');
      const init = optsRef.current;
      // Sync the on-screen chart (data source + axes the user picked) so the
      // agent edits THAT chart, not a blank one.
      const liveSpec = init?.getSpec?.() ?? init?.initSpec;
      ws.send(JSON.stringify({ type: 'init', chart_id: init?.chartId, spec: liveSpec }));
      lastFrameAt = Date.now();
      keepalive = window.setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;
        // SILENCE IS DEATH (AG-2364). Every ping has had a pong to answer it and
        // a turn has had a heartbeat besides; nothing for this long means the
        // other leg is gone. Close it ourselves so `onclose` can do what it does
        // for every other drop — a half-open socket never fires it on its own.
        if (Date.now() - lastFrameAt > SILENCE_MS) {
          silenced = true;
          try {
            ws.close();
          } catch {
            /* noop */
          }
          return;
        }
        ws.send(JSON.stringify({ type: 'ping' }));
      }, 25_000);
    };

    ws.onclose = (e) => {
      if (!alive) return;
      window.clearTimeout(timeout);
      if (keepalive) window.clearInterval(keepalive);
      // WAS A TURN IN FLIGHT? Clearing `busy` and `progress` is right — neither
      // survives the socket — but doing ONLY that is what made a dropped build
      // look like nothing had ever been asked: the steps vanished, the spinner
      // stopped, and the transcript was left showing the user's prompt and no
      // reply, with no way to tell a finished turn from an abandoned one.
      const wasMidTurn = busyRef.current;
      setBusy(false);
      setProgress([]);
      if (stoppingRef.current) {
        // `stop()` closed it and already bumped the epoch: this reopens by
        // itself, and calling it a disconnection would be a lie.
        stoppingRef.current = false;
        setConnState((s) => (s === 'error' ? s : 'closed'));
        return;
      }
      // A refused token is not a transport problem. Retrying cannot mint a new
      // credential, and a loop would hammer the auth path, so stop and say what
      // the person has to do.
      if (e.code === AUTH_CLOSE) {
        authFailedRef.current = true;
        setConnState('error');
        setConnError(
          (prev) => prev || 'Your session is no longer valid. Sign in again to reconnect the assistant.',
        );
        return;
      }
      // DO NOT DECLARE THE TURN LOST YET (AG-2178).
      //
      // The build runs on the server and does not stop because this socket did.
      // The reconnect below fires in about a second, and the server's `init`
      // reply either hands the board back or says it has nothing — and it now
      // says which, in `resumed`. Posting an error here meant a person watching
      // got a red "connection dropped" for a build that was fine, seconds before
      // the board itself arrived. Three times in a row on a live board.
      //
      // So the turn is remembered, not reported. `resumed` decides.
      if (wasMidTurn) pendingTurnRef.current = true;
      // A clean close from either side leaves nothing to recover — unless it is
      // the close THIS client asked for because the socket had gone silent, which
      // is a drop wearing a clean code.
      if (!silenced && (e.code === 1000 || e.code === 1005)) {
        setConnState((s) => (s === 'error' ? s : 'closed'));
        return;
      }
      // Anything else is an unexpected drop — the case this whole block exists
      // for. Stay on `connecting` so the panel reads "Connecting…" and pulses
      // rather than showing a dead-end Disconnected notice with a button.
      const attempt = retryRef.current;
      retryRef.current = attempt + 1;
      setConnState((s) => (s === 'error' ? s : 'connecting'));
      if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = window.setTimeout(
        () => setEpoch((x) => x + 1),
        Math.min(1000 * 2 ** attempt, MAX_RETRY_MS),
      );
    };

    ws.onerror = () => {
      /* `onclose` carries the outcome; an error alone is not reportable. */
    };

    // THE REPORT COMES DOWN WHEN THE TURN'S OWN RESULT ARRIVES. A resume that
    // found nothing is the server's best answer at that second; the frames it
    // held for this session can still land on this or a later socket.
    const retractLostTurn = () => {
      const id = lostTurnRef.current;
      if (!id) return;
      lostTurnRef.current = null;
      setMessages((m) => m.filter((x) => x.id !== id));
    };

    ws.onmessage = (e) => {
      lastFrameAt = Date.now();
      let f: Frame;
      try {
        f = JSON.parse(e.data) as Frame;
      } catch {
        return;
      }
      switch (f.type) {
        case 'token':
          setMessages((m) => {
            const last = m[m.length - 1];
            if (last && last.role === 'assistant' && last.streaming) {
              return [...m.slice(0, -1), { ...last, text: last.text + f.delta }];
            }
            return [...m, { id: nextId(), role: 'assistant', text: f.delta, streaming: true }];
          });
          break;
        case 'status': {
          const label = phaseLabel(f);
          setProgress((steps) => {
            const done = steps.map((s) => ({ ...s, done: true }));
            if (done.length && done[done.length - 1].label === label) return steps;
            return [...done, { id: ++stepSeq.current, label, done: false }].slice(-8);
          });
          break;
        }
        case 'chart_update':
          setChart(f.data);
          break;
        case 'spec':
          setSpec(f.spec);
          break;
        case 'dashboard':
          retractLostTurn();
          setDashboard((d) => ({
            id: f.dashboard_id,
            name: f.name,
            embedUrl: f.embed_url ?? null,
            views: f.views ?? [],
            completion: f.completion ?? null,
            nonce: (d?.nonce ?? 0) + 1,
          }));
          break;
        case 'transcript':
          // The conversation this session already had, replayed by the backend
          // on `init`. Only adopted when we have nothing of our own: a live
          // transcript is the truth, and a replay arriving mid-conversation
          // (a reconnect during a turn) must not overwrite it.
          setMessages((m) =>
            m.length
              ? m
              : (f.items || [])
                  .filter((it) => it && it.text)
                  .map((it) => ({
                    id: nextId(),
                    role: it.role === 'user' ? ('user' as const) : ('assistant' as const),
                    text: it.text,
                  })),
          );
          break;
        case 'gap':
          setGap({
            recovery: f.recovery || '',
            reason: f.reason || '',
            message: f.message || '',
            candidates: f.candidates ?? [],
            questions: f.questions ?? [],
            ask: lastAskRef.current,
          });
          break;
        case 'design_reference':
          setDesignReferences(Number(f.count) || 0);
          break;
        case 'suggestions':
          setSuggestions(f.items || []);
          break;
        case 'message':
          retractLostTurn();
          setMessages((m) => [...m, { id: nextId(), role: 'assistant', text: f.text }]);
          break;
        case 'error':
          connErrorSeen.current = true;
          setConnError(f.message);
          setMessages((m) => [...m, { id: nextId(), role: 'assistant', text: f.message, tone: 'error' }]);
          setBusy(false);
          setProgress([]);
          break;
        case 'resumed': {
          // THE RECONNECT'S VERDICT, and the only place a dropped turn is
          // reported. `recovered` means the board finished after the socket went
          // away and has just been handed back — there is nothing to apologise
          // for and the `dashboard` frame above has already shown it.
          if (pendingTurnRef.current) {
            // ⭐ STILL RUNNING IS NOT LOST (AG-2282). The socket drops and this
            // client reconnects within a second, while the build still has
            // minutes to go. The server now says so, and the only honest thing
            // to do is keep waiting — the turn stays pending, the chat stays in
            // its working state, and the result arrives on this socket when the
            // build finishes. Reporting a loss here is what made two completed
            // boards on 2026-09-18 read as failures.
            if (f.pending) {
              // The socket went, the TURN did not. `onclose` cleared `busy`
              // because nothing survives a closed socket — but the build does,
              // so put the chat back into its working state and keep waiting.
              // The result arrives on this socket when the build finishes.
              setBusy(true);
              // `onclose` also cleared `progress` to `[]`, and the checklist
              // only renders when `progress.length > 0` — so a build that has
              // no more `status` frames left to send (it is already on its
              // last phase when the drop happens, as measured live: the next
              // frames to arrive were `message`/`dashboard`/`done`, no
              // `status` in between) left the panel with a Stop button and
              // nothing else for however long the remaining wait was. Reseed
              // one step so "still working" is visible again immediately.
              setProgress((steps) =>
                steps.length ? steps : [{ id: ++stepSeq.current, label: 'Reconnected — still working', done: false }],
              );
              break;
            }
            // A non-pending resume CONCLUDES the turn, either way: the board was
            // handed back, or there is none to hand back. Both end the waiting.
            pendingTurnRef.current = false;
            setBusy(false);
            if (!f.recovered) {
              const lostId = nextId();
              lostTurnRef.current = lostId;
              setMessages((m) => [...m, {
                id: lostId,
                role: 'assistant',
                text: 'The connection dropped while that was running, and the assistant has reconnected without finding a finished board. Send the request again.',
                tone: 'error',
              }]);
            }
          }
          break;
        }
        case 'done':
          setMessages((m) => m.map((x) => (x.streaming ? { ...x, streaming: false } : x)));
          setBusy(false);
          setProgress((steps) => steps.map((s) => ({ ...s, done: true })));
          break;
      }
    };

    return () => {
      alive = false;
      if (keepalive) window.clearInterval(keepalive);
      // Safe in both cases: on a reconnect this id has already fired (clearing a
      // fired timeout is a no-op), and on unmount it cancels a pending retry so
      // a closed panel stops dialling.
      if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
      ws.close();
      wsRef.current = null;
    };
  }, [sessionId, epoch, setBusy]);

  // A slept laptop, a wifi hop, a VPN flap: the browser knows the moment the
  // network could work again, which beats waiting out whatever backoff we are
  // currently sitting on.
  useEffect(() => {
    const kick = () => {
      if (authFailedRef.current) return;
      if (document.visibilityState === 'hidden') return;
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
      if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
      retryRef.current = 0;
      setEpoch((x) => x + 1);
    };
    window.addEventListener('online', kick);
    document.addEventListener('visibilitychange', kick);
    return () => {
      window.removeEventListener('online', kick);
      document.removeEventListener('visibilitychange', kick);
    };
  }, []);

  const send = useCallback((text: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !text.trim()) return;
    // Re-sync the live builder spec right before each turn, so manual edits the
    // user just made are what the agent operates on (bidirectional sync).
    const liveSpec = optsRef.current?.getSpec?.();
    if (liveSpec) ws.send(JSON.stringify({ type: 'set_spec', spec: liveSpec }));
    setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
    setBusy(true);
    setProgress([{ id: ++stepSeq.current, label: 'Thinking', done: false }]);
    ws.send(JSON.stringify({ type: 'user_message', text }));
  }, [setBusy]);

  /**
   * Address the DASHBOARD AGENT rather than the chart builder.
   *
   * Which one runs is declared here, by the caller, and not inferred from the
   * sentence. The backend used to choose with a keyword test — a dashboard noun
   * near a build verb — so "add a tab for SLA risk" was read as a chart edit
   * because it contained no build verb. A message type cannot be misread.
   *
   * With a board id it EDITS that board incrementally (the tiles the user did not
   * mention are left alone); without one it builds a new board.
   */
  const sendDashboard = useCallback((text: string, options?: SendDashboardOptions) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !text.trim()) return;
    setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
    setBusy(true);
    // A new turn supersedes the last gap, and remembers the ask so a recovery
    // can re-run it without the user retyping what they already said.
    setGap(null);
    lastAskRef.current = text;
    setProgress([
      {
        id: ++stepSeq.current,
        label: options?.dashboardId ? 'Reading the dashboard' : 'Planning the build',
        done: false,
      },
    ]);
    ws.send(
      JSON.stringify(
        options?.dashboardId
          ? { type: 'dashboard_edit', dashboard_id: options.dashboardId, text, share: options?.share === true }
          : {
              type: 'dashboard_build',
              text,
              ...(options?.sourceIds?.length ? { source_ids: options.sourceIds } : {}),
              // Sent on every turn, never omitted: an omitted flag is what the
              // server reads as "publish" (AG-2031).
              share: options?.share === true,
            },
      ),
    );
  }, [setBusy]);

  /**
   * Attach (or clear) DESIGN REFERENCES the next build should match — images and
   * website URLs, screenshotted server-side. Another capability the backend has
   * always had and nothing surfaced.
   */
  const setDesignReference = useCallback((refs: { images?: string[]; urls?: string[] }) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
      JSON.stringify({
        type: 'set_design_reference',
        images: refs.images ?? [],
        urls: refs.urls ?? [],
      }),
    );
  }, []);

  /**
   * Stop waiting on the current turn.
   *
   * There is no cancel frame in the protocol, so this closes the socket (which
   * ends the server's turn) and immediately reopens it. The conversation history
   * is kept server-side by the checkpointer, keyed on the session id.
   */
  const stop = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    stoppingRef.current = true;
    setBusy(false);
    setProgress([]);
    setMessages((m) => {
      const last = m[m.length - 1];
      const closed = last?.streaming ? [...m.slice(0, -1), { ...last, streaming: false }] : m;
      return [...closed, { id: nextId(), role: 'assistant', text: 'Stopped.', tone: 'stopped' }];
    });
    try {
      ws.close(1000, 'stopped by user');
    } catch {
      /* already closing */
    }
    setEpoch((e) => e + 1);
  }, [setBusy]);

  /**
   * Reopen a dropped connection now.
   *
   * Reconnection is automatic; this is the impatient path — it cancels the
   * pending backoff and retries immediately, and clears the auth latch so a
   * person who has just signed in again can get straight back in.
   */
  const reconnect = useCallback(() => {
    if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
    retryRef.current = 0;
    authFailedRef.current = false;
    setConnError('');
    setEpoch((e) => e + 1);
  }, []);

  /** Clear the on-screen transcript (the server-side thread is untouched). */
  const clear = useCallback(() => {
    setMessages([]);
    setProgress([]);
    setSuggestions([]);
  }, []);

  return {
    connState,
    connected: connState === 'open',
    connError,
    messages,
    progress,
    busy,
    chart,
    spec,
    dashboard,
    /** Why the last turn stopped without a board, when it did. */
    gap,
    suggestions,
    designReferences,
    send,
    sendDashboard,
    setDesignReference,
    stop,
    reconnect,
    clear,
  };
}

export type ChartAgent = ReturnType<typeof useChartSocket>;
